<template>
	<div>
		<Filters :mode="mode" :categories="categories" :salles="salles" @change-mode="changeMode"
			@select-categorie="selectCategorie" @select-salle="selectSalle" @deselectCategorie="deselectCategorie"
			@deselectSalle="deselectSalle" @search="updateSearch" @resetFilters="resetFilters"
			@select-range="selectRange" @goToPrevMonth="goToPrevMonth" @goToNextMonth="goToNextMonth"
			@setRange="setRange" />
		<Grid v-if="mode === 'grid'" :dates="filteredDates" :pagination="pagination" @change-page="changePage"
			@resetFilters="resetFilters" />
		<List v-if="mode === 'calendar'" :dates="filteredDates" :pagination="pagination" @change-page="changePage"
			@select-range="selectRange" @resetFilters="resetFilters" @goToPrevMonth="goToPrevMonth"
			@goToNextMonth="goToNextMonth" @setRange="setRange" />
		<template v-if="otherResults.length > 0">
			<div v-html="otherResults"></div>
		</template>
	</div>
</template>

<script setup>
import { ref, computed, provide, onMounted } from 'vue';
import Filters from '@/js/components/programmation/Filters.vue';
import Grid from '@/js/components/programmation/Grid.vue';
import List from '@/js/components/programmation/List.vue';
import Featured from '@/js/components/programmation/Featured.vue';
import Fuse from 'fuse.js';

const mode = ref('home');
const props = defineProps(['dates', 'cat']);
const categories = ref(props.dates.categories);
const salles = ref(props.dates.salles);
const paged = ref(1);
const pageSize = ref(24);
const pageCount = ref(0);
const search = ref('');
const otherResults = ref('');
const calendarData = ref({
	startDate: null,
	endDate: null,
	range: {
		start: null,
		end: null,
	},
	now: new Date(),
	firstDay: null,
	startingSelectedDate: null,
});

provide('calendarData', calendarData);

calendarData.value.firstDay = new Date(calendarData.value.now.getFullYear(), calendarData.value.now.getMonth(), 1);

onMounted(() => {
	if (props.cat) {
		const cat = categories.value.find(c => c.term_id == props.cat);
		if (cat) {
			cat.selected = true;
			mode.value = 'grid';
		}
	}

	// check url query lieu exists
	if (window.location.search.includes('lieu')) {
		const params = new URLSearchParams(window.location.search);
		const lieu = params.get('lieu');
		const salle = salles.value.find(s => s.term_id == lieu);
		if (salle) {
			salle.selected = true;
			mode.value = 'grid';
		}
	}

	// check url query cat exists
	if (window.location.search.includes('categorie')) {
		const params = new URLSearchParams(window.location.search);
		const cats = params.get('categorie');
		if (cats) {
			const catArray = cats.split(',').map(c => parseInt(c));
			categories.value.forEach(c => {
				if (catArray.includes(parseInt(c.term_id))) {
					c.selected = true;
				}
			});
			mode.value = 'grid';
		}
	}

	// check url query start and end exists
	if (window.location.search.includes('start') && window.location.search.includes('end')) {
		const params = new URLSearchParams(window.location.search);
		const start = params.get('start');
		const end = params.get('end');
		if (start && end) {
			selectRange({
				start: new Date(start + ' 00:00:00'),
				end: new Date(end + ' 23:59:59'),
			});
		}
	}

	// check url query search exists
	if (window.location.search.includes('search')) {
		const params = new URLSearchParams(window.location.search);
		const searchQuery = params.get('search');
		if (searchQuery) {
			search.value = searchQuery;
			updateSearch(searchQuery);
			mode.value = 'grid';
		}
	}

	// check url query paged exists
	if (window.location.search.includes('page')) {
		const params = new URLSearchParams(window.location.search);
		const page = params.get('page');
		if (page) {
			paged.value = parseInt(page);
		}
	}

	// check url query mode exists
	if (window.location.search.includes('mode')) {
		const params = new URLSearchParams(window.location.search);
		const modeQuery = params.get('mode');
		if (modeQuery) {
			mode.value = modeQuery;
		}
	}
});

const selectRange = (range) => {
	if (range.start && range.end) {
		calendarData.value.startDate = range.start;
		calendarData.value.endDate = range.end;
	} else {
		calendarData.value.startDate = null;
		calendarData.value.endDate = null;
	}
	if (mode.value == 'home') mode.value = 'grid';
	if (mode.value == 'calendar') scrollToTop();
	updateURL();
}

const changePage = (page) => {
	paged.value = page;
	window.locomotiveScroll.scrollTo(0, {
		duration: 0.5,
		lerp: 0,
	});
	updateURL();
}

const selectedCategories = computed(() => {
	return categories.value.filter(c => c.selected).map(c => parseInt(c.term_id));
});
const selectedSalles = computed(() => {
	return salles.value.filter(s => s.selected).map(s => parseInt(s.term_id));
});

const selectCategorie = (categorie) => {
	if (selectedCategories.value.includes(categorie)) return;
	categories.value[categories.value.findIndex(c => c.term_id === categorie)].selected = true;
	if (mode.value == 'home') mode.value = 'grid';
	updateURL();
}

const selectSalle = (salle) => {
	if (selectedSalles.value.includes(salle)) return;
	salles.value[salles.value.findIndex(s => s.term_id === salle)].selected = true;
	if (mode.value == 'home') mode.value = 'grid';
	updateURL();
}

const deselectCategorie = (categorie) => {
	if (!selectedCategories.value.includes(categorie)) return;
	categories.value[categories.value.findIndex(c => c.term_id === categorie)].selected = false;
	updateURL();
}

const deselectSalle = (salle) => {
	if (!selectedSalles.value.includes(salle)) return;
	salles.value[salles.value.findIndex(s => s.term_id === salle)].selected = false;
	updateURL();
}

const resetFilters = () => {
	categories.value.forEach(c => c.selected = false);
	salles.value.forEach(s => s.selected = false);
	calendarData.value.startDate = null;
	calendarData.value.endDate = null;
	calendarData.value.range.start = null;
	calendarData.value.range.end = null;
	search.value = '';
	paged.value = 1;
	updateURL();
}

const updateSearch = (value) => {
	search.value = value;

	fetch(WPURLS.apiurl + 'search?query=' + value, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
	})
		.then(response => response.json())
		.then(data => {
			otherResults.value = data.content;
			if (data.found_posts == 0) otherResults.value = '';
			if (mode.value == 'home') mode.value = 'grid';
			updateURL();
		})
		.catch(error => {
			//console.error('Error:', error);
		});
}

const filteredDates = computed(() => {
	let dates = props.dates.dates.filter(date => {
		const matchesCategorie = selectedCategories.value.length > 0 ? date.categories.map(c => parseInt(c.term_id)).filter(c => selectedCategories.value.includes(c)).length > 0 : true;
		const matchesSalle = selectedSalles.value.length > 0 ? selectedSalles.value.includes(parseInt(date.lieu)) : true;
		let matchesDate = true;

		if (calendarData.value.startDate && calendarData.value.endDate) {
			const d = new Date(date.date + ' 00:00:00');
			const start = calendarData.value.startDate;
			const end = new Date(calendarData.value.endDate);
			end.setHours(23, 59, 59, 999);
			matchesDate = d >= start && d <= end;
		}

		return matchesCategorie && matchesSalle && matchesDate;
	});

	if (search.value.length > 1) {
		// Helper function to normalize text for searching: strip apostrophes/quotes and accents
		const normalizeText = (text) => {
			if (!text) return '';
			return String(text)
				.replace(/[''""]/g, "")
				.normalize('NFD')
				.replace(/\p{Diacritic}/gu, "");
		};

		const normalizedSearch = normalizeText(search.value).trim();
		
		const fuse = new Fuse(dates, {
			shouldSort: false,
			threshold: 0.2,
			ignoreLocation: true,
			keys: [
				{
					name: 'artiste',
					getFn: (item) => normalizeText(item.artiste)
				},
				{
					name: 'evenement',
					getFn: (item) => normalizeText(item.evenement)
				},
				{
					name: 'categories.name',
					getFn: (item) => {
						if (!item.categories || !Array.isArray(item.categories)) return '';
						return item.categories.map(cat => normalizeText(cat.name)).join(' ');
					}
				}
			]
		});
		const filtered = fuse.search(normalizedSearch);
		dates = filtered.map(result => result.item);
	}
	pageCount.value = Math.ceil(dates.length / pageSize.value);
	const start = (paged.value - 1) * pageSize.value;
	const end = start + pageSize.value;
	dates = dates.slice(start, end);
	return dates;
});

const pagination = computed(() => {
	return {
		page: paged.value,
		pageCount: pageCount.value,
		pageSize: pageSize.value,
	}
});

const changeMode = (newMode) => {
	mode.value = newMode;

	// add class to body
	if (newMode === 'grid') {
		document.body.classList.add('grid-mode');
		document.body.classList.remove('list-mode');
	} else {
		document.body.classList.remove('grid-mode');
		document.body.classList.add('list-mode');
	}
	updateURL();
}

const goToPrevMonth = () => {
	calendarData.value.firstDay = new Date(calendarData.value.firstDay.getFullYear(), calendarData.value.firstDay.getMonth() - 1, 1);
};

const goToNextMonth = () => {
	calendarData.value.firstDay = new Date(calendarData.value.firstDay.getFullYear(), calendarData.value.firstDay.getMonth() + 1, 1);
};

const setRange = (day, hover = false) => {
	if (hover && calendarData.value.startingSelectedDate != null) {
		if (day.date < calendarData.value.startingSelectedDate) {
			calendarData.value.range.start = day.date;
			calendarData.value.range.end = calendarData.value.startingSelectedDate;
		} else {
			calendarData.value.range.start = calendarData.value.startingSelectedDate;
			calendarData.value.range.end = day.date;
		}
		return;
	} else if (hover) return;

	if (day.day == null) {
		calendarData.value.range.start = null;
		calendarData.value.range.end = null;
		return;
	}

	if (calendarData.value.startingSelectedDate == null) {
		calendarData.value.startingSelectedDate = day.date;
		calendarData.value.range.start = day.date;
		calendarData.value.range.end = null;
	} else if (day.date < calendarData.value.startingSelectedDate) {
		calendarData.value.range.start = day.date;
		calendarData.value.range.end = calendarData.value.startingSelectedDate;
	} else {
		calendarData.value.range.start = calendarData.value.startingSelectedDate;
		calendarData.value.range.end = day.date;
	}

	if (calendarData.value.range.start != null && calendarData.value.range.end != null) {
		selectRange({
			start: calendarData.value.range.start,
			end: calendarData.value.range.end,
		});
		calendarData.value.startingSelectedDate = null;
	}
};

const updateURL = () => {
	const params = new URLSearchParams(window.location.search);
	if (selectedCategories.value.length > 0) {
		params.set('categorie', selectedCategories.value.join(','));
	} else {
		params.delete('categorie');
	}
	if (selectedSalles.value.length > 0) {
		params.set('lieu', selectedSalles.value.join(','));
	} else {
		params.delete('lieu');
	}
	if (calendarData.value.startDate && calendarData.value.endDate) {
		params.set('start', calendarData.value.startDate.toISOString().split('T')[0]);
		params.set('end', calendarData.value.endDate.toISOString().split('T')[0]);
	} else {
		params.delete('start');
		params.delete('end');
	}
	if (search.value.length > 0) {
		params.set('recherche', search.value);
	} else {
		params.delete('recherche');
	}
	// add page parameter
	params.set('page', paged.value);
	// add mode parameter
	params.set('mode', mode.value);
	window.history.replaceState({}, '', '?' + params.toString());
};

const scrollToTop = () => {
	console.log(document.querySelector('[data-module="Programmation"]').offsetTop);
	window.scrollTo({
		top: document.querySelector('[data-module="Programmation"]').offsetTop,
		behavior: 'instant',
	});
};
</script>
